import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPersonRunning } from '@fortawesome/free-solid-svg-icons';

const CELL_SIZE = 11;
const GAP = 2;
const LABEL_WIDTH = 30;

const COLORS = ['#ebedf0', '#DCEAF2', '#AAC4D6', '#5B7EA4', '#3d5a80'];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateLabel = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const ActivityHeatmap = ({ runs }) => {
  const [tooltip, setTooltip] = useState(null);

  const { grid, monthLabels, totalRuns, totalDistance, getLevel } = useMemo(() => {
    const dateMap = {};
    let totalRuns = 0;
    let totalDistance = 0;

    (runs || []).forEach((run) => {
      const date = run.run_date;
      if (!date) return;
      if (!dateMap[date]) dateMap[date] = { count: 0, distance: 0 };
      dateMap[date].count += 1;
      dateMap[date].distance += parseFloat(run.distance_km) || 0;
      totalRuns += 1;
      totalDistance += parseFloat(run.distance_km) || 0;
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(today);
    start.setFullYear(start.getFullYear() - 1);
    start.setDate(start.getDate() - start.getDay());

    const grid = [];
    const monthLabels = [];
    const current = new Date(start);
    let prevMonth = -1;

    while (current <= today || grid.length === 0) {
      const week = [];

      for (let d = 0; d < 7; d++) {
        const dateStr = toDateStr(current);

        if (d === 0 && current.getMonth() !== prevMonth) {
          monthLabels.push({
            label: current.toLocaleString('en-US', { month: 'short' }),
            weekIndex: grid.length,
          });
          prevMonth = current.getMonth();
        }

        week.push({
          date: dateStr,
          data: dateMap[dateStr] || null,
          isFuture: current > today,
        });

        current.setDate(current.getDate() + 1);
      }

      grid.push(week);
      if (grid.length > 53) break;
    }

    const distances = Object.values(dateMap)
      .map((d) => d.distance)
      .filter((d) => d > 0)
      .sort((a, b) => a - b);

    let thresholds;
    if (distances.length >= 4) {
      thresholds = [
        distances[Math.floor(distances.length * 0.25)],
        distances[Math.floor(distances.length * 0.5)],
        distances[Math.floor(distances.length * 0.75)],
      ];
    } else if (distances.length > 0) {
      const max = distances[distances.length - 1];
      thresholds = [max * 0.25, max * 0.5, max * 0.75];
    } else {
      thresholds = [1, 3, 5];
    }

    const getLevel = (data) => {
      if (!data || data.distance === 0) return 0;
      if (data.distance <= thresholds[0]) return 1;
      if (data.distance <= thresholds[1]) return 2;
      if (data.distance <= thresholds[2]) return 3;
      return 4;
    };

    return { grid, monthLabels, totalRuns, totalDistance, getLevel };
  }, [runs]);

  const handleMouseEnter = (e, day) => {
    if (day.isFuture) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      ...day,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
  };

  return (
    <div className="scrapbook-container">
      <div className="flex items-center gap-2 mb-4">
        <FontAwesomeIcon
          icon={faPersonRunning}
          className="w-5 h-5 text-cool-steel"
        />
        <h2 className="font-heading font-semibold text-lg">Run Activity</h2>
        <span className="text-sm text-gray-400 ml-auto">
          {totalRuns} run{totalRuns !== 1 ? 's' : ''} &middot;{' '}
          {totalDistance.toFixed(1)}km in the last year
        </span>
      </div>

      <div className="overflow-x-auto pb-1">
        <div style={{ display: 'inline-block' }}>
          {/* Month labels */}
          <div
            className="flex text-xs text-gray-400"
            style={{ paddingLeft: `${LABEL_WIDTH}px`, marginBottom: '4px' }}
          >
            {monthLabels.map((m, i) => {
              const nextIndex =
                monthLabels[i + 1]?.weekIndex ?? grid.length;
              const span = nextIndex - m.weekIndex;
              return (
                <div
                  key={`${m.label}-${i}`}
                  style={{ width: `${span * (CELL_SIZE + GAP)}px` }}
                >
                  {span >= 3 ? m.label : ''}
                </div>
              );
            })}
          </div>

          {/* Grid with day labels */}
          <div className="flex">
            {/* Day labels */}
            <div
              className="flex flex-col text-xs text-gray-400"
              style={{ width: `${LABEL_WIDTH}px`, gap: `${GAP}px` }}
            >
              {DAY_LABELS.map((label, i) => (
                <div
                  key={i}
                  style={{
                    height: `${CELL_SIZE}px`,
                    lineHeight: `${CELL_SIZE}px`,
                    fontSize: '10px',
                  }}
                >
                  {i % 2 === 1 ? label : ''}
                </div>
              ))}
            </div>

            {/* Week columns */}
            <div className="flex" style={{ gap: `${GAP}px` }}>
              {grid.map((week, wi) => (
                <div
                  key={wi}
                  className="flex flex-col"
                  style={{ gap: `${GAP}px` }}
                >
                  {week.map((day, di) => (
                    <div
                      key={di}
                      className="rounded-[2px]"
                      style={{
                        width: `${CELL_SIZE}px`,
                        height: `${CELL_SIZE}px`,
                        backgroundColor: day.isFuture
                          ? 'transparent'
                          : COLORS[getLevel(day.data)],
                        outline:
                          tooltip?.date === day.date
                            ? '2px solid #5B7EA4'
                            : 'none',
                        outlineOffset: '-1px',
                      }}
                      onMouseEnter={(e) => handleMouseEnter(e, day)}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-1.5 mt-3 justify-end text-xs text-gray-400">
        <span>Less</span>
        {COLORS.map((color, i) => (
          <div
            key={i}
            className="rounded-[2px]"
            style={{
              width: `${CELL_SIZE}px`,
              height: `${CELL_SIZE}px`,
              backgroundColor: color,
            }}
          />
        ))}
        <span>More</span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 px-3 py-2 text-white text-xs rounded-lg shadow-lg pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
            backgroundColor: '#2A2E36',
          }}
        >
          <div className="font-semibold">
            {tooltip.data
              ? `${tooltip.data.distance.toFixed(1)}km — ${tooltip.data.count} run${tooltip.data.count !== 1 ? 's' : ''}`
              : 'No runs'}
          </div>
          <div className="text-gray-300">{formatDateLabel(tooltip.date)}</div>
        </div>
      )}
    </div>
  );
};

export default ActivityHeatmap;
